/**
 * Native (Expo) push token bridge.
 *
 * Wrapper contract (window.NativeApp):
 *   getPushPermission()   -> Promise<'granted'|'denied'|'undetermined'>
 *   requestPushPermission() -> Promise<'granted'|'denied'>
 *   getPushToken()        -> Promise<string|null>   // ExponentPushToken[...]
 *   setBadge(n)
 *   event 'nativeapp-push-token' { detail: { token } } when the token rotates
 */
import axios from 'axios';
import { isNativeApp, nativePlatform } from './nativeBridge';
import { getOrCreateDeviceId } from './deviceToken';

const API = process.env.REACT_APP_BACKEND_URL;
const LAST_KEY = 'akf_native_push_token';

export const nativePushSupported = () =>
  isNativeApp() && typeof window.NativeApp?.getPushToken === 'function';

export async function nativePushPermission() {
  try {
    if (typeof window.NativeApp?.getPushPermission === 'function') {
      return (await window.NativeApp.getPushPermission()) || 'undetermined';
    }
  } catch { /* noop */ }
  return 'undetermined';
}

export async function registerNativePushToken({ requestPermission = false } = {}) {
  if (!nativePushSupported()) return { ok: false, reason: 'unsupported' };
  try {
    let perm = await nativePushPermission();
    if (perm !== 'granted' && requestPermission && typeof window.NativeApp.requestPushPermission === 'function') {
      perm = (await window.NativeApp.requestPushPermission()) || 'denied';
    }
    if (perm !== 'granted') return { ok: false, reason: perm };
    const token = await window.NativeApp.getPushToken();
    if (!token) return { ok: false, reason: 'no_token' };
    await axios.post(`${API}/api/push-notifications/native-device`, {
      token,
      platform: String(nativePlatform() || 'ios').toLowerCase() === 'android' ? 'android' : 'ios',
      device_id: getOrCreateDeviceId(),
      device_label: /iPad/.test(navigator.userAgent) ? 'iPad' : (/iPhone/.test(navigator.userAgent) ? 'iPhone' : 'Android device'),
    }, { withCredentials: true });
    try { localStorage.setItem(LAST_KEY, token); } catch { /* noop */ }
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: e?.message || 'error' };
  }
}

export async function unregisterNativePushToken() {
  if (!isNativeApp()) return;
  let token = null;
  try { token = localStorage.getItem(LAST_KEY); localStorage.removeItem(LAST_KEY); } catch { /* noop */ }
  try {
    await axios.delete(`${API}/api/push-notifications/native-device`, { data: { token }, withCredentials: true });
  } catch { /* fire-and-forget */ }
}

/** Re-register whenever the wrapper reports a rotated token (signed-in only). */
if (typeof window !== 'undefined') {
  window.addEventListener('nativeapp-push-token', () => {
    try { if (localStorage.getItem('akountfly_user')) registerNativePushToken(); } catch { /* noop */ }
  });
}
